import { Modal } from '@phx/design-system';
import { useDisclosure } from '@phx/design-system/hooks';
import {
    HighlightPharmacyCard,
    PharmacyCard,
    type Pricing,
} from '@phx/myphx-lib';
import { useTranslation } from 'react-i18next';

import { getFragment, getFragments } from '../../../graphql';
import {
    type PharmacyProviderInfoFragment,
    type ProviderOffersInfoFragment,
    SuggestedProviderOffersReason,
} from '../../../graphql/generated/graphql';
import { usePatientContext } from '../../../hooks/use-patient-context';
import { getOffersByProviderId } from '../../../util/offers/get-offers-by-provider-id';
import { getPriceableOffers } from '../../../util/offers/get-priceable-offers';
import { getTodaysHours } from '../../../util/pharmacy/get-todays-hours';
import { getPrimaryProvider } from '../../../util/provider-offers/provider-offers-utils';

import { MoreProviders } from './MoreProviders';

export const ProviderOffersCard = ({
    providerOffers,
    onSelected,
    suggestedReasons,
    role,
}: {
    providerOffers: ProviderOffersInfoFragment;
    onSelected: (id: string, providerId?: string) => void;
    suggestedReasons?: SuggestedProviderOffersReason[];
    role?: React.AriaRole;
}) => {
    const { t } = useTranslation();
    const { preferences } = usePatientContext();
    const { favoritePharmacies } = preferences;
    const { checkIsFavorite, toggleFavorite } = favoritePharmacies;
    const [opened, { open, close }] = useDisclosure(false);
    const primaryPharmacy = getPrimaryProvider(providerOffers);
    if (!primaryPharmacy) {
        throw new Error('No pharmacy for offer');
    }

    const description =
        primaryPharmacy.__typename === 'PharmacyProvider'
            ? [
                  t('common.distance.distanceWithUnit', {
                      miles: Math.max(0.1, providerOffers.distance).toFixed(1),
                  }),
                  primaryPharmacy.description ?? '',
              ]
            : [t('offerPharmacyCard.deliveryTime')]; //NOTE: The delivery time is hard coded as we don't have actual data for that yet

    const additionalInfo =
        primaryPharmacy.__typename === 'PharmacyProvider'
            ? getTodaysHours(primaryPharmacy)
            : t('offerPharmacyCard.deliveryCost'); //NOTE: The delivery cost is hard coded as we don't have actual data for that yet

    const offers = getOffersByProviderId(
        getPriceableOffers(providerOffers),
        primaryPharmacy.id
    );
    const pricing = offers.map((offer) => offer.pricingDetail as Pricing);

    let morePharmacies: PharmacyProviderInfoFragment[] = [];

    if (providerOffers.provider.__typename === 'ChainProvider') {
        const chainProvider = getFragment(providerOffers.provider);
        if (chainProvider?.pharmacies?.length) {
            morePharmacies = getFragments(chainProvider.pharmacies.slice(1)); //exclude primary provider
        }
    }

    const footer = (
        <MoreProviders
            primaryName={primaryPharmacy.name}
            moreProviders={morePharmacies}
            onSelected={(id: string) => onSelected(providerOffers.id, id)}
        />
    );

    if (
        suggestedReasons &&
        suggestedReasons.includes(SuggestedProviderOffersReason.Cheapest)
    ) {
        const fulfillmentType =
            providerOffers.provider.__typename === 'MailOrderProvider'
                ? t('offerPharmacyCard.delivery')
                : t('offerPharmacyCard.pickup');
        const heading = `${t('offerPharmacyCard.greatValue.heading')} (${fulfillmentType})`;

        return (
            <>
                <HighlightPharmacyCard
                    role={role}
                    title={primaryPharmacy.name}
                    favorite={checkIsFavorite(primaryPharmacy.id)}
                    toggleFavorite={() => {
                        toggleFavorite(primaryPharmacy.id);
                    }}
                    description={description}
                    pricing={pricing}
                    additionalInfo={additionalInfo}
                    onClick={() => onSelected(providerOffers.id)}
                    variant="GreatValue"
                    header={heading}
                    onIconClick={open}
                    footer={footer}
                />
                <Modal
                    title={t('offerPharmacyCard.greatValue.modal.title')}
                    onClose={close}
                    opened={opened}
                >
                    {t('offerPharmacyCard.greatValue.modal.content')}
                </Modal>
            </>
        );
    }

    const subTitle = pricing.length
        ? undefined
        : t('offerPharmacyCard.contactForPrice');

    return (
        <PharmacyCard
            role={role}
            title={primaryPharmacy.name}
            favorite={checkIsFavorite(primaryPharmacy.id)}
            toggleFavorite={() => {
                toggleFavorite(primaryPharmacy.id);
            }}
            description={description}
            additionalInfo={additionalInfo}
            pricing={pricing}
            onClick={() => {
                onSelected(providerOffers.id);
            }}
            footer={footer}
            subtitle={subTitle}
        />
    );
};
